import { Mutator, getIn } from 'final-form';
import { trimErrorObjectByFormSchema } from './trimErrorObjectByFormSchema';
import { ValidationErrorObject } from '../../types/validationError';
import { Dispatch, SetStateAction } from 'react';
import { searchForRenderedFields } from './searchForRenderedFields';

type MutatorArgs = [
    setBackendValidationErrors: Dispatch<SetStateAction<ValidationErrorObject>>,
    submittedErrors: Record<string, unknown>,
    parentSectionId: string,
    sectionId: string,
    errorObjectWithPaths: ValidationErrorObject['errorObjectWithPaths'],
    arrayElementId: string,
    parentArrayElementId: string
];

/**
 * Remove submit error messages that already appear in client-side
 * validation errors for the same field, to avoid duplicate display.
 * Client-side errors (meta.error) are newline-joined strings;
 * submit errors (meta.submitError) are string arrays.
 */
export const removeOverlappingErrors = (
    submitErrors: Record<string, unknown>,
    validationErrors: Record<string, unknown>,
    path = ''
): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    for (const key in submitErrors) {
        if (key.startsWith('_')) {
            result[key] = submitErrors[key];
            continue;
        }
        const fieldPath = path ? `${path}.${key}` : key;
        const submitValue = submitErrors[key];
        const validationValue = getIn(validationErrors ?? {}, fieldPath);

        if (
            Array.isArray(submitValue) &&
            submitValue.every((v) => typeof v === 'string') &&
            typeof validationValue === 'string'
        ) {
            const clientMessages = validationValue
                .split('\n')
                .map((m: string) => m.trim());
            const filtered = submitValue.filter(
                (msg: string) => !clientMessages.includes(msg.trim())
            );
            if (filtered.length > 0) {
                result[key] = filtered;
            }
        } else if (
            submitValue &&
            typeof submitValue === 'object' &&
            !Array.isArray(submitValue)
        ) {
            result[key] = removeOverlappingErrors(
                submitValue as Record<string, unknown>,
                validationErrors ?? {},
                fieldPath
            );
        } else {
            result[key] = submitValue;
        }
    }
    return result;
};

export const backendErrorSubmitMutator: Mutator = (
    args: MutatorArgs,
    state
) => {
    const [
        setBackendValidationErrors,
        submittedErrors,
        parentSectionId,
        sectionId,
        errorObjectWithPaths,
        arrayElementId,
        parentArrayElementId,
    ] = args;

    if (submittedErrors && sectionId && errorObjectWithPaths) {
        const renderedFieldsWithErrors = searchForRenderedFields(
            errorObjectWithPaths[parentSectionId || sectionId]?.map(
                (errorObjects) => errorObjects.fieldPath
            )
        );
        setBackendValidationErrors((currentState) => ({
            ...currentState,
            renderedFieldsWithErrors,
        }));

        const errors = trimErrorObjectByFormSchema(
            submittedErrors,
            errorObjectWithPaths,
            parentSectionId,
            sectionId,
            parentArrayElementId,
            arrayElementId
        );

        const submitErrors = errors ?? {};
        const cleanedSubmitErrors = removeOverlappingErrors(
            submitErrors,
            state.formState.errors as Record<string, unknown>
        );

        if (errors) {
            state.formState = {
                ...state.formState,
                submitFailed: true,
                submitErrors: cleanedSubmitErrors,
            };
        } else {
            state.formState = {
                ...state.formState,
                submitFailed: false,
                submitErrors: {},
            };
        }
    }
};
